import { useState, useEffect, useRef, useMemo } from 'react';
import { DailySchedule, ServiceOpportunity, SearchParameters } from '../models';
import { localStorageManager } from '../services/LocalStorageManager';
import { formatLocalDateAsISOString } from '../utils/dateUtils';

interface UseSearchProps {
  // Search context
  searchParams: SearchParameters | null; // Use the composite object

  // MonthData hook dependencies
  monthData: any;
  monthDataIsLoading: boolean;
  monthDataError: string | null;
  currentSelectedDay: string | null;
  setCurrentSelectedDay: (date: string | null) => void;
  setViewDate: (date: string) => void;
  refreshCurrentMonth: () => Promise<void>;
  
  // SearchManagement hook dependency
  checkAndAutoUpdateSearch: (dateOfInterest: Date) => Promise<boolean>;
  setLocationDetails: (details: any) => void;
  
  // Tab navigation callback
  onSearchComplete?: () => void;
}

export function useSearch({
  searchParams, // Use the composite object
  monthData,
  monthDataIsLoading,
  monthDataError,
  currentSelectedDay,
  setCurrentSelectedDay,
  setViewDate,
  refreshCurrentMonth,
  checkAndAutoUpdateSearch,
  setLocationDetails,
  onSearchComplete
}: UseSearchProps) {
  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [optimizing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [schedules, setSchedules] = useState<Record<string, DailySchedule>>({});
  const [optimizedSchedules, setOptimizedSchedules] = useState<Record<string, DailySchedule>>({});
  const [showOptimized, setShowOptimized] = useState<boolean>(false);
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<Set<string>>(new Set());
  
  // Track whether a search has been explicitly initiated
  const [hasSearchBeenInitiated, setHasSearchBeenInitiated] = useState<boolean>(false);
  
  // Using the shared utility function for ID generation
  // imported from '../utils/idUtils'
  
  // Calculate active schedules based on the new optimized indexes model
  const activeSchedules = useMemo(() => {
    if (!showOptimized) return schedules;
    
    // Create optimized schedules using the original schedules + optimized indexes
    const result: Record<string, DailySchedule> = {};
    
    Object.keys(schedules).forEach(dateKey => {
      const schedule = schedules[dateKey];
      const optimizedIndexes = monthData?.optimizedScheduleIndexes?.[dateKey] || [];
      
      if (schedule && optimizedIndexes.length > 0) {
        // Create a new schedule with only the optimized opportunities
        const filteredOpportunities = optimizedIndexes
          .map((index: number) => schedule.opportunities[index])
          .filter(Boolean); // Remove any undefined entries
        
        result[dateKey] = {
          ...schedule,
          opportunities: filteredOpportunities
        };
      } else {
        // Fall back to the original schedule if no optimized data
        result[dateKey] = schedule;
      }
    });
    
    return result;
  }, [schedules, monthData?.optimizedScheduleIndexes, showOptimized]);
  
  // Get the current schedule
  const currentSchedule = useMemo(() => {
    if (!currentSelectedDay) return null;
    return activeSchedules[currentSelectedDay] || schedules[currentSelectedDay] || null;
  }, [currentSelectedDay, activeSchedules, schedules]);
  
  // Get opportunities for the current schedule - wrapped in useMemo to fix hook dependency warning
  const currentOpportunities = useMemo(() => {
    return currentSchedule?.opportunities || [];
  }, [currentSchedule]);
  
  // Selected opportunities derived from IDs
  const selectedOpportunities = useMemo(() => {
    if (!currentOpportunities || selectedOpportunityIds.size === 0) return [];
    
    return currentOpportunities.filter(opportunity => 
      selectedOpportunityIds.has(opportunity.id)
    );
  }, [currentOpportunities, selectedOpportunityIds]);
  
  // Toggle selection of an opportunity
  const toggleOpportunitySelection = (opportunity: ServiceOpportunity) => {
    const opportunityId = opportunity.id;
    
    setSelectedOpportunityIds(prev => {
      const newSet = new Set(prev);
      
      if (newSet.has(opportunityId)) {
        newSet.delete(opportunityId);
      } else {
        newSet.add(opportunityId);
      }
      
      // Save to local storage if we have a selected day
      if (currentSelectedDay) {
        localStorageManager.saveSelectedOpportunities(
          currentSelectedDay,
          Array.from(newSet)
        );
      }
      
      return newSet;
    });
  };
  
  // Combined loading state - only consider monthDataIsLoading if a search has been initiated
  const isLoading = loading || (hasSearchBeenInitiated && monthDataIsLoading) || optimizing;
  
  // Search execution - now uses searchParams from props
  const handleSearch = async () => {
    if (!searchParams) {
      setError('Search parameters are not set.');
      console.error('handleSearch called without searchParams');
      return;
    }

    const { location, dateOfInterest, services } = searchParams;
    const locationId = location?.id;

    // Validate the required parameters
    if (!location || !locationId || !dateOfInterest || !services || services.length === 0) {
      setError('Please select a location, date, and at least one service.');
      console.error('handleSearch called with incomplete searchParams:', searchParams);
      return;
    }
    
    // Format the date for view setting (ISO format YYYY-MM-DD) using timezone-safe formatter
    const formattedDate = formatLocalDateAsISOString(dateOfInterest);
    console.log(`useSearch: Setting date to ${formattedDate} (timezone-safe)`);
    setViewDate(formattedDate);

    // Auto-update the currently loaded search if auto-save is enabled
    await checkAndAutoUpdateSearch(dateOfInterest);
    
    // Set search initiated flag to true
    setHasSearchBeenInitiated(true);
    
    // Force refresh data
    setLoading(true);
    try {
      await refreshCurrentMonth();
      
      // Notify that search is complete, so tabs can be switched
      if (onSearchComplete) {
        onSearchComplete();
      }
    } catch (err) {
      console.error('Error in search:', err);
      setError('Failed to fetch service opportunities. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  // Select date - load selections for the new date from localStorage
  const handleSelectDate = (date: string) => {
    // Update the selected day first
    setCurrentSelectedDay(date);
    
    // Load any saved selections for this date
    const savedSelections = localStorageManager.getSelectedOpportunities(date);
    setSelectedOpportunityIds(new Set(savedSelections));
  };
  
  // Update schedules when monthData changes
  useEffect(() => {
    if (monthData) {
      // Store the current schedule dates before updating
      const currentScheduleDates = Object.keys(schedules).sort().join(',');
      
      setSchedules(monthData.dailySchedulesByDate);
      // No longer using separate optimizedSchedules collection
      // We now derive optimized schedules from indexes
      setLocationDetails(monthData.locationDetails);
      
      // If there's an error from month data fetch, update our error state
      if (monthDataError) {
        setError(monthDataError);
      }
      
      // Get the new schedule dates
      const newScheduleDates = Object.keys(monthData.dailySchedulesByDate).sort().join(',');
      
      // Only reset selections if we have different dates in the schedule
      // This prevents losing selections when just refreshing the same data
      if (currentScheduleDates !== newScheduleDates && currentScheduleDates !== '') {
        console.log('Date range changed, resetting selected opportunities');
        setSelectedOpportunityIds(new Set());
      }
      
      // Store the new schedules for future comparison
      prevSchedulesRef.current = Object.keys(monthData.dailySchedulesByDate);
    }
  }, [monthData, monthDataError, setLocationDetails, schedules]);
  
  // Load selected opportunities from localStorage when current day changes
  useEffect(() => {
    if (currentSelectedDay) {
      const savedSelections = localStorageManager.getSelectedOpportunities(currentSelectedDay);
      setSelectedOpportunityIds(new Set(savedSelections));
    }
  }, [currentSelectedDay]);
  
  // Set hasSearchBeenInitiated to true if we have schedules
  useEffect(() => {
    if (Object.keys(schedules).length > 0 && !hasSearchBeenInitiated) {
      setHasSearchBeenInitiated(true);
    }
  }, [schedules, hasSearchBeenInitiated]);
  
  // Track previous schedules to detect significant data changes
  const prevSchedulesRef = useRef<string[]>([]);
  
  // Enhanced function to clear search results without affecting local storage
  const clearSearchResults = () => {
    // Clear all schedules data
    setSchedules({});
    setOptimizedSchedules({}); // Also clear optimized schedules
    
    // Reset selected opportunity IDs in memory only
    setSelectedOpportunityIds(new Set());
    
    // Reset the current selected day
    if (setCurrentSelectedDay) {
      setCurrentSelectedDay(null);
    }
    
    // Reset the hasSearchBeenInitiated flag
    setHasSearchBeenInitiated(false);
    
    // Clear any errors
    setError(null);
    
    // Reset optimization display
    setShowOptimized(false);
  };

  return {
    // States
    loading,
    optimizing,
    isLoading,
    error,
    setError,
    schedules,
    optimizedSchedules,
    showOptimized,
    setShowOptimized,
    selectedOpportunityIds,
    hasSearchBeenInitiated,
    
    // Derived values
    activeSchedules,
    currentSchedule,
    currentOpportunities,
    selectedOpportunities,
    tideHeightStatistics: monthData?.tideHeightStatistics, // Expose tide height statistics
    
    // Functions
    handleSearch,
    handleSelectDate,
    toggleOpportunitySelection,
    clearSearchResults // Export the new function
  };
}
